import { isDefined, isEmpty, isNonEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { FormattedMessage } from 'components/shared/Intl/FormattedMessage';
import { useFetchDetailsAndUpdateTags } from 'hooks/tags/use-fetch-details';
import { ClipboardEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import Select, { GroupBase, SelectComponentsConfig, SelectInstance } from 'react-select';
import { useDebounceCallback } from 'usehooks-ts';
import { getClipboardData } from 'utils/events/events';

import type { TagOption, TagsProps } from './model';

const EMPTY_OPTIONS = [] as const;
export function TagsPicker<T>({
  filterTags,
  onEditingKeyDown,
  tags,
  isDisabled,
  isFocused,
  lostInputBeforeEditing,
  convertPastedLabelsToTags,
  id,
  placeholder,
  className,
  leftIcon,
  hasErrors,
  isClearable = true,
  isMenuListNarrow = false,
  autoFocus = false,
  formatOptionLabel,
  getOptionLabel,
  onBlur,
  tabIndex,
  btnTabIndex = 0,
  canDeleteAll,
  isMulti,
  onTagsChanged,
  components,
  classNames: selectClassNames,
  onInputChanged,
}: TagsProps<T>) {
  type IsMulti = typeof isMulti;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<readonly TagOption<T>[]>([]);
  const selectRef = useRef<SelectInstance<TagOption<T>, IsMulti>>(null);

  const search = useCallback(
    async (inputValue: string) => {
      if (isEmpty(inputValue)) {
        return;
      }
      setIsLoading(true);
      try {
        const filteredTags = await filterTags(inputValue);
        setOptions(filteredTags);
      } finally {
        setIsLoading(false);
      }
    },
    [filterTags],
  );
  const searchOptions = useDebounceCallback(search, 500);

  const handleInputChanged = useCallback(
    (inputValue: string) => {
      const hasValue = isNonEmpty(inputValue);
      setInputValue(inputValue);
      setIsMenuOpen(hasValue);
      setOptions(options => (hasValue ? options : EMPTY_OPTIONS));
      searchOptions(inputValue);
      onInputChanged?.(inputValue);
    },
    [searchOptions, onInputChanged],
  );
  useEffect(() => {
    if (isFocused) {
      selectRef.current?.focus();
      if (isDefined(lostInputBeforeEditing)) {
        handleInputChanged(lostInputBeforeEditing);
      }
    }
    if (!isFocused) {
      setIsMenuOpen(false);
    }
  }, [isFocused, lostInputBeforeEditing, handleInputChanged]);

  const resetState = useCallback(() => {
    setOptions([]);
    setInputValue('');
    setIsLoading(false);
    setIsMenuOpen(false);
  }, []);

  const onKeyDown = (e: KeyboardEvent): void => {
    if (inputValue.length === 0 && onEditingKeyDown) {
      onEditingKeyDown(e);
    }
  };
  const onChanged = useCallback(
    (newValue: readonly TagOption<T>[] | TagOption<T> | undefined): void => {
      resetState();
      if (isMulti) {
        onTagsChanged(newValue as readonly TagOption<T>[]);
      } else {
        onTagsChanged((newValue as TagOption<T>) ?? undefined);
      }
    },
    [onTagsChanged, isMulti, resetState],
  );
  const onTagsArrayChanged = useCallback(
    (tags: readonly TagOption<T>[]) => {
      if (isMulti) {
        onChanged(tags);
      } else {
        onChanged(tags.at(0));
      }
    },
    [isMulti, onChanged],
  );

  const fetchDetailsAndUpdateTags = useFetchDetailsAndUpdateTags({
    convertPastedLabelsToTags,
    isMulti,
    onChanged,
    tags,
  });
  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>): void => {
      event.preventDefault();
      if (isDisabled) {
        return;
      }
      const pastedValue = getClipboardData(event);
      fetchDetailsAndUpdateTags(pastedValue);
    },
    [fetchDetailsAndUpdateTags, isDisabled],
  );

  const onAllTagsRemoved = () => {
    onTagsArrayChanged([]);
  };

  return (
    <div
      className={classNames('form-control tags-container h-100 text-info p-0 d-flex', className, {
        'is-warning': hasErrors,
        // @ts-ignore Assuming that the options handled are underlyings, needs to be moved up in the component stack
        'text-warning': !isMulti && tags[0]?.rawValue?.isRestricted,
      })}
      data-e2e={`${id}-container`}
      onPaste={handlePaste}
    >
      {leftIcon && <i className="icon input-group-text bg-transparent pe-0">{leftIcon}</i>}
      <Select<TagOption<T>, IsMulti>
        menuIsOpen={isMenuOpen}
        autoFocus={autoFocus}
        id={`${id}-select`}
        inputId={id}
        className="underlying-manual flex-grow-1"
        classNamePrefix="select"
        isClearable={isClearable}
        isDisabled={isDisabled}
        isLoading={isLoading}
        loadingMessage={() => <FormattedMessage id="loading" />}
        isSearchable
        isMulti={isMulti}
        styles={{
          control: provided => ({ ...provided, minHeight: 32 }),
          container: provided => ({ ...provided }),
        }}
        classNames={{
          container: () => 'text-secondary',
          input: () => 'p-0 m-0 text-primary',
          placeholder: () => 'text-secondary',
          valueContainer: () => 'px-2 text-info',
          menuList: () => 'position-absolute',
          menu: () => (isMenuListNarrow ? 'auto z-3' : 'w-100 z-3'),
          loadingIndicator: () => 'pe-2',
          dropdownIndicator: () => 'd-none',
          indicatorSeparator: () => 'd-none',
          indicatorsContainer: () => 'cursor-pointer',
          ...selectClassNames,
        }}
        unstyled
        components={{
          ...(components as SelectComponentsConfig<TagOption<T>, IsMulti, GroupBase<TagOption<T>>>),
        }}
        filterOption={null}
        formatOptionLabel={formatOptionLabel}
        inputValue={inputValue}
        name={id}
        options={options}
        value={tags}
        placeholder={placeholder}
        closeMenuOnSelect
        openMenuOnFocus={false}
        openMenuOnClick={false}
        getOptionLabel={getOptionLabel}
        noOptionsMessage={() => placeholder || ''}
        onChange={t => onChanged(t as readonly TagOption<T>[] | TagOption<T> | undefined)}
        onInputChange={handleInputChanged}
        onBlur={e => {
          onBlur?.(e);
        }}
        blurInputOnSelect={false}
        ref={selectRef}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
      />
      {canDeleteAll ? (
        <button
          data-e2e={`${id}-delete-all-btn`}
          disabled={tags.length === 0}
          onClick={onAllTagsRemoved}
          type="button"
          title="Delete all"
          className="btn btn-default btn-icon btn-delete-all-tags"
          tabIndex={btnTabIndex}
        >
          <i className="icon">delete</i>
        </button>
      ) : null}
    </div>
  );
}
